import type { FieldInputs } from "~/layers/ui/modules/VDynamicForm/runtime/types";

export type ConfirmationInput = {
  title: string;
  subtitle?: string;
  message?: string;
  doneText?: string;
  cancelText?: string;
  inputs?: FieldInputs;
  width?: number | string;
  height?: number | string;
  maxHeight?: number | string;
  noPadContent?: boolean;
  inputsDefaults?: unknown;
};

export type ConfirmationLikeInput<A extends any[]> =
  | ConfirmationInput
  | { (...a: A): ConfirmationInput | boolean };

export type ConfirmData = {
  id: string;
  options: ConfirmationInput;
  closing: boolean;
  timeoutId?: NodeJS.Timeout;
  confirm: (inputs?: any) => void;
  cancel: () => void;
};

export const useConfirmStore = () => {
  const store = useState<ConfirmData[]>("confirm-store", () => []);

  const remove = (id: string) => {
    // Clear timeout if it exists
    const entry = store.value.find((o) => o.id === id);
    if (entry?.timeoutId) {
      clearTimeout(entry.timeoutId);
    }
    store.value = unref(store).filter((o) => o.id !== id);
  };

  const markAsClosing = (id: string) => {
    // Replace the array to ensure reactivity and store timeout
    const timeoutId = setTimeout(() => remove(id), 1000);
    store.value = unref(store).map((entry) => {
      if (entry.id === id) {
        return { ...entry, closing: true, timeoutId };
      }
      return entry;
    });
  };

  const add = <R>(options: ConfirmationInput, fn?: { (inputs: any): R }) => {
    return new Promise<R | false>((resolve, reject) => {
      const id = uuid();
      const rmFn = () => {
        // Mark as closing immediately to prevent reopening
        markAsClosing(id);
      };
      const confirm = async (inputs?: any) => {
        return new Promise<void>((resolve2, reject2) => {
          const done = (r?: any) => {
            rmFn();
            resolve(r);
            resolve2();
          };

          const fail = () => {
            resolve(false);
            reject2();
          };

          if (fn) {
            try {
              Promise.resolve(fn(inputs)).then(done).catch(fail);
            } catch {
              fail();
            }
          } else {
            done();
          }
        });
      };
      const cancel = () => {
        reject();
        rmFn();
      };

      store.value.push({
        id,
        confirm,
        cancel,
        closing: false,
        options: {
          doneText: "Confirm",
          cancelText: "Cancel",
          ...options,
        },
      });
    });
  };

  const cleanup = () => {
    // Clear all pending timeouts
    store.value.forEach((entry) => {
      if (entry.timeoutId) {
        clearTimeout(entry.timeoutId);
      }
    });
    store.value = [];
  };

  return {
    entries: readonly(store),
    add,
    cleanup,
  };
};
